// Walley payment confirmation for the "thank you" page.
//
// Walley redirects the customer back as soon as the iframe is done, but the
// reservation is only marked paid once the backend has captured the money
// (seconds for a card, up to ~14 minutes, or never, for an invoice that is
// waiting for Walley's approval). So the page asks api4 for the payment status
// and only shows "thank you" when it is `paid`.

// `checking`: the backend flagged the payment for customer service (partly captured, captured late, outcome
// unknown). It is not final: the page says it is being looked at and keeps asking, because customer service
// may still finish the payment.
import type {WalleyPaymentStatus} from './api/walley/WalleyApi';
export type {WalleyPaymentStatus};

/**
 * What the poller reports: a real status, `unknown` when the status could not be read (the checks go on, more
 * slowly, in case api4 comes back), or `timeout` when the time budget ran out while the payment was still pending
 * (the checks have stopped; nothing is known to be final).
 */
export type WalleyPollResult = WalleyPaymentStatus | 'unknown' | 'timeout';

const STORAGE_KEY = 'walleyPayment';

/** Remember the checkout's public token so the page can prove it started the payment after the redirect. */
export function storeWalleyPaymentToken(resId: number, publicToken: string): void {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify({ resId, publicToken }));
  } catch { /* storage unavailable: the page then shows the unconfirmed screen (never a success) */ }
}

export function readWalleyPaymentToken(resId: string | number): string | undefined {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return undefined;
    const parsed = JSON.parse(raw) as { resId?: number | string; publicToken?: string };
    if (String(parsed.resId ?? '') !== String(resId) || !parsed.publicToken) return undefined;
    return parsed.publicToken;
  } catch {
    return undefined;
  }
}

/**
 * Which screen follows the redirect back from Walley. A browser that started the payment can ask api4 for its
 * status; any other browser cannot know, so it is told that the status is unconfirmed (never "thank you").
 */
export function walleyReturnScreen(resId: string | number): 'walley_confirming' | 'walley_unconfirmed' {
  return readWalleyPaymentToken(resId) ? 'walley_confirming' : 'walley_unconfirmed';
}

/**
 * Which result screen follows a failed checkout request. `already_paid` means api4 refused a second checkout
 * because the reservation is paid (or its payment is being processed): the customer is shown the payment status,
 * never a failure and never a new payment form.
 */
export function walleyScreenAfterCheckoutError(
  errMessage: string, resId: string | number,
): 'walley_confirming' | 'walley_unconfirmed' | 'vehicle_unavailable' | 'fail' {
  if (errMessage === 'already_paid') return walleyReturnScreen(resId);
  return errMessage === 'vehicle_no_longer_available' ? 'vehicle_unavailable' : 'fail';
}

/**
 * The reservation a payment result screen is about. After Walley's redirect the address carries `?r=`; when the
 * payment step was opened again (and refused as already paid) it does not, and the booking in the session
 * (`rid-1`) tells. Empty when neither does.
 */
export function walleyResultReservationId(search: string): string {
  const fromUrl = new URLSearchParams(search).get('r');
  if (fromUrl) return fromUrl;
  try {
    const raw = sessionStorage.getItem('rid-1');
    const resId = raw ? (JSON.parse(raw) as { resId?: number | string })?.resId : undefined;
    return resId && Number(resId) > 0 ? String(resId) : '';
  } catch {
    return '';
  }
}

export function clearWalleyPaymentToken(): void {
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch { /* ignore */ }
}

export interface PollOptions {
  /** Delay between checks during the first `fastForMs`. */
  fastIntervalMs?: number;
  /** Delay between checks after that. */
  slowIntervalMs?: number;
  fastForMs?: number;
  /** Stop asking after this long (the backend gives up capturing after ~14 min). */
  maxMs?: number;
  /** Consecutive failed requests before reporting `unknown`. */
  maxErrors?: number;
}

const DEFAULTS: Required<PollOptions> = {
  fastIntervalMs: 2000,
  slowIntervalMs: 5000,
  fastForMs: 30000,
  maxMs: 16 * 60 * 1000,
  maxErrors: 5,
};

/**
 * Ask for the status until it is final (`paid` / `failed`).
 *
 * `onStatus` is called with every non-final status as it changes and once with
 * the final result. Reports `unknown` after `maxErrors` failed reads in a row (api4
 * unreachable or unknown token), so the caller can take the customer off the
 * spinner, but keeps asking at the slow pace: if api4 answers again, its status is
 * reported as usual, `paid` included. When the time budget runs out while the payment is
 * still pending, polling stops and `timeout` is reported: running out of time is
 * not a success, and the caller decides what stays on screen.
 *
 * Returns a function that stops the polling. Once stopped, nothing more is
 * reported, not even the answer to a request that was already in flight.
 */
export function pollWalleyPayment(
  fetchStatus: () => Promise<WalleyPaymentStatus>,
  onStatus: (status: WalleyPollResult) => void,
  options: PollOptions = {},
): () => void {
  const opts = { ...DEFAULTS, ...options };
  const startedAt = Date.now();
  let stopped = false;
  let errors = 0;
  let lastReported: WalleyPollResult | undefined;
  let timer: ReturnType<typeof setTimeout> | undefined;

  const report = (status: WalleyPollResult) => {
    if (stopped || status === lastReported) return;
    lastReported = status;
    onStatus(status);
  };

  const finish = (status: WalleyPollResult) => {
    if (stopped) return;
    stopped = true;
    if (timer) clearTimeout(timer);
    onStatus(status);
  };

  const tick = async () => {
    if (stopped) return;
    try {
      const status = await fetchStatus();
      errors = 0;
      if (status === 'paid' || status === 'failed') {
        finish(status);
        return;
      }
      report(status);
    } catch {
      errors += 1;
      // Not final: api4 may come back, and a payment it then reports as paid still gets its thank-you page
      // (and the GA purchase, which is sent only from there).
      if (errors >= opts.maxErrors) report('unknown');
    }

    if (stopped) return;
    const elapsed = Date.now() - startedAt;
    if (elapsed >= opts.maxMs) {
      finish('timeout');
      return;
    }
    const failing = errors >= opts.maxErrors;
    timer = setTimeout(tick, elapsed < opts.fastForMs && !failing ? opts.fastIntervalMs : opts.slowIntervalMs);
  };

  void tick();

  return () => {
    stopped = true;
    if (timer) clearTimeout(timer);
  };
}
